import React from 'react'
import { inject, observer } from 'mobx-react'
import {
    AutoComplete,
    Button,
    DatePicker,
    Icon,
    Input,
    Modal,
    Popconfirm,
    Select,
    Table,
    TimePicker,
    Tooltip,
    message
} from 'antd'
import { ColumnProps } from 'antd/lib/table'
import moment from 'moment'
import mentorsService from '@services/mentors'
import { alertPopup } from '@utils/popup'
import {
    STATUS_LABELS,
    OCCURRED_CHOICES,
    renderStatusCell,
    isMeetingDocumentable,
    isBeforeMeetingsStart,
    FutureMeetingTag
} from '@utils/meetingStatus'
import { COMMISSION_SELECT_STYLES } from '@pages/Clients/Agent/Commissions/CreateNewCommission/commissionSelectStyles'

const { Option } = Select
const AutoOption = AutoComplete.Option

// maps the api's known error bodies (see MentorsMeetingsController.js) to their Hebrew equivalent
const MEETING_ERROR_MESSAGES: { [key: string]: string } = {
    'duplicate meeting for this agent and time': 'כבר קיימת פגישה לסוכן זה באותו מועד',
    'invalid status': 'סטטוס לא תקין',
    'meeting_time is required': 'יש להזין מועד פגישה',
    'agent_id, or contact_name and contact_phone, are required': 'יש להזין סוכן או שם וטלפון איש קשר',
    'agent not found': 'הסוכן לא נמצא',
    'mentor not found': 'המנטור לא נמצא',
    'meeting not found': 'הפגישה לא נמצאה'
}

const meetingErrorBody = (error, fallback: string) => {
    const data = error && error.response && error.response.data
    return typeof data === 'string' && MEETING_ERROR_MESSAGES[data] ? MEETING_ERROR_MESSAGES[data] : fallback
}

// below this the phone is too partial to be worth a request — mirrors the api's own minimum
const MIN_PHONE_DIGITS = 3
const SEARCH_DEBOUNCE_MS = 350

// the server ignores anything shorter, so do not even send it
const MIN_TERM_CHARS = 2

// phones are stored and sent as bare digits; 20 is well past any real number, including a country code
const MAX_PHONE_DIGITS = 20

// dropping a match clears every field it filled, so a new search can never keep the old person's details.
// the field being typed into is written back over this by the caller
const CLEARED_MATCH_FIELDS = {
    selectedUser: null,
    matchedContact: null,
    fullName: '',
    email: '',
    address: '',
    agentName: '',
    mentorId: undefined,
    meetings: []
}

// which box the coordinator is identifying the person from. Once a match is picked every
// other box locks, so the found person can never be saved under different details —
// that one box stays editable, and editing it drops the match and frees the rest
type SearchField = 'phone' | 'fullName' | 'agentName' | 'email'

// every dropdown shows the person the same way, whichever box is being typed in:
// just phone and name — enough to tell people apart without a label that runs off the box
const LABEL_ORDER: SearchField[] = ['fullName', 'phone']

// antd 3.23.4 clips every dropdown option (nowrap + overflow hidden + ellipsis), and the
// dropdown is only as wide as its input, so "name - phone" lost whichever half came first.
// let the option wrap and let the panel grow past the input instead of trimming the label.
const MATCH_DROPDOWN_STYLES = `
    .meeting-match-dropdown {
        max-width: 150px !important;
        width: auto !important;
    }
    .meeting-match-dropdown .ant-select-dropdown-menu-item {
        white-space: normal;
        overflow: visible;
        text-overflow: clip;
    }
`

// the match note points the coordinator at whichever box is still editable
const SEARCH_FIELD_LABELS: { [key: string]: string } = {
    phone: 'הטלפון',
    fullName: 'השם המלא',
    agentName: 'שם הסוכן',
    email: 'האימייל'
}

// a match is either a users row (source 'user', has an id) or the details of an earlier
// meeting booked against a bare phone (source 'meeting', no id — stays a contact)
interface Match {
    id?: number
    source: 'user' | 'meeting'
    first_name: string
    last_name: string
    phone: string
    email: string
    address: string
    agent_name?: string
    mentor: string
}

const fullNameOf = (match: Match) => `${match.first_name || ''} ${match.last_name || ''}`.trim()

// some rows are stored without the leading 0 ("501234567") — put it back for display,
// so the field shows the phone the way the user knows it
const withLeadingZero = (phone: string) => {
    const raw = String(phone || '').trim()
    return /^[1-9]\d{8}$/.test(raw) ? `0${raw}` : raw
}

// what a match holds for a given box — both the option label and the value written on select
const valueOf = (match: Match, field: SearchField) => {
    if (field === 'fullName') return fullNameOf(match)
    if (field === 'phone') return withLeadingZero(match.phone)
    if (field === 'agentName') return match.agent_name || ''
    return match.email || ''
}

const EMPTY_FORM = {
    fullName: '',
    phone: '',
    email: '',
    address: '',
    agentName: '',
    matches: [] as Match[],
    // the matched users row, when there is one — drives agent_id on submit
    selectedUser: null as Match | null,
    // a contact recovered from a past meeting: autofills the form but stays a contact_* meeting
    matchedContact: null as Match | null,
    // the box the person was identified from — the only one left editable once a match is picked
    searchField: 'phone' as SearchField,
    mentorId: undefined,
    meetingTime: null,
    hourPicked: false,
    status: 'not_documented',
    saving: false
}

/**
 * One meeting modal for both entry points.
 *
 *   mode="agent"  — the agent is fixed (agent page); mentor shows as a pill when the agent has one
 *   mode="search" — the person is found by phone (coordinators tab), and may be outside the system
 *
 * Both modes share the add-meeting form and the meeting-history table; in search mode the
 * history only appears once the phone matches a registered agent.
 */
class MeetingModal extends React.Component<any, any> {
    _isMounted: boolean
    searchTimer: any

    constructor(props) {
        super(props)
        this._isMounted = false
        this.searchTimer = null
        this.state = {
            ...EMPTY_FORM,
            mentorsList: [],
            meetings: [],
            historyLoading: false,
            editingMeetingId: null,
            historyVisible: false
        }
    }

    componentDidMount() {
        this._isMounted = true
        this.loadMentors()
        if (this.props.visible) this.onOpen()
    }

    componentDidUpdate(prevProps) {
        // the parent keeps this mounted and toggles `visible`, so opening is a prop change, not a mount
        if (this.props.visible && !prevProps.visible) this.onOpen()

        // the agent page mounts this modal before its agent request resolves, so the mentors list can
        // land while mentor_id is still undefined. Apply the default once the agent's details arrive.
        const previousMentorId = prevProps.agentInfo && prevProps.agentInfo.mentor_id
        const currentMentorId = this.props.agentInfo && this.props.agentInfo.mentor_id
        if (this.isAgentMode && currentMentorId && currentMentorId !== previousMentorId) {
            this.setState({ mentorId: this.agentDefaultMentorId() })
        }
    }

    componentWillUnmount() {
        this._isMounted = false
        clearTimeout(this.searchTimer)
    }

    get isAgentMode() {
        return this.props.mode === 'agent'
    }

    onOpen = () => {
        if (this.isAgentMode) this.loadAgentHistory(this.agentId())
    }

    agentId = () => {
        const { agentInfo } = this.props
        return agentInfo && agentInfo.id ? agentInfo.id : null
    }

    // the agent's own mentor, but only when the dropdown actually offers it. The list drops hidden
    // mentors, so a missing one stays undefined and the select shows its placeholder instead
    // (antd 3.x renders the bare id when no Option matches the value)
    agentDefaultMentorId = (list?: any[]) => {
        const { agentInfo } = this.props
        if (!this.isAgentMode || !agentInfo || !agentInfo.mentor_id) return undefined
        const mentors = list || this.state.mentorsList
        const listed = mentors.filter((m: any) => m.id === agentInfo.mentor_id)[0]
        return listed ? agentInfo.mentor_id : undefined
    }

    loadMentors = () => {
        mentorsService
            .getDashboardMentors({})
            .then(res => {
                if (!this._isMounted) return
                const list = res.data || []
                this.setState({ mentorsList: list, mentorId: this.agentDefaultMentorId(list) })
            })
            .catch(() => {
                if (this._isMounted) alertPopup({ type: 'error', title: 'שגיאה בטעינת רשימת מנטורים', body: '' })
            })
    }

    // the history list also carries the agent's address, used to prefill the form.
    // an off-system contact has no agent id — their past meetings are found by the
    // details instead, so pass those along whenever there is no id to look up by
    loadAgentHistory = (agentId: number, contact?: { phone?: string; name?: string; email?: string }) => {
        if (!agentId && !contact) return
        this.setState({ historyLoading: true })
        mentorsService
            .getAgentMeetings(agentId, contact || {})
            .then(res => {
                if (!this._isMounted) return
                const data = res.data || {}
                this.setState({
                    meetings: data.meetings || [],
                    // only prefill while the field is untouched, so reloading never overwrites an edit
                    address: this.state.address || data.address || '',
                    historyLoading: false
                })
            })
            .catch(() => {
                if (this._isMounted) this.setState({ historyLoading: false })
            })
    }

    scheduleSearch = (phone: string) => {
        clearTimeout(this.searchTimer)

        if (phone.replace(/\D/g, '').length < MIN_PHONE_DIGITS) {
            this.setState({ matches: [] })
            return
        }

        this.searchTimer = setTimeout(() => {
            mentorsService
                .searchUserByPhone(phone)
                .then(res => {
                    if (this._isMounted) this.setState({ matches: res.data || [] })
                })
                .catch(() => {
                    if (this._isMounted) this.setState({ matches: [] })
                })
        }, SEARCH_DEBOUNCE_MS)
    }

    // same search, entered from the name / agent-name / email boxes instead of the phone box
    scheduleTermSearch = (term: string) => {
        clearTimeout(this.searchTimer)

        if (term.trim().length < MIN_TERM_CHARS) {
            this.setState({ matches: [] })
            return
        }

        this.searchTimer = setTimeout(() => {
            mentorsService
                .searchUserByTerm(term.trim())
                .then(res => {
                    if (this._isMounted) this.setState({ matches: res.data || [] })
                })
                .catch(() => {
                    if (this._isMounted) this.setState({ matches: [] })
                })
        }, SEARCH_DEBOUNCE_MS)
    }

    // one handler for the three text boxes: type to edit the field and search on what was typed.
    // editing any of them also drops a match, exactly like editing the phone does
    handleTermChange = (field: 'fullName' | 'agentName' | 'email') => value => {
        const raw = value === undefined || value === null ? '' : String(value)
        // names are names — digits are dropped as they are typed. An email may of course hold them
        const text = field === 'email' ? raw : raw.replace(/\d/g, '')
        const { selectedUser, matchedContact } = this.state
        const current = selectedUser || matchedContact

        // picking an option fires onChange with the same text too — only a real edit drops the match
        if (current && text === this.state[field]) return

        // the agency name never identifies the person and never locks, so editing it keeps the
        // match intact — it is free text on the meeting itself. No re-search either: typing an
        // agency here is correcting a detail, not looking someone up
        if (field === 'agentName' && current) {
            this.setState({ agentName: text })
            return
        }

        // typing here makes this the identifying box, so it is the one that stays editable
        this.setState(
            current
                ? { ...CLEARED_MATCH_FIELDS, searchField: field, [field]: text }
                : ({ searchField: field, [field]: text } as any)
        )
        this.scheduleTermSearch(text)
    }

    handlePhoneChange = value => {
        const raw = value === undefined || value === null ? '' : String(value)
        // the field takes digits only — separators, '+' and letters are dropped as they are typed
        const phone = raw.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS)
        const { selectedUser, matchedContact } = this.state
        const current = selectedUser || matchedContact

        // picking an option fires onChange with that same phone too — only a real edit drops the match
        if (current && phone === withLeadingZero(current.phone)) return

        this.setState(
            current ? { ...CLEARED_MATCH_FIELDS, searchField: 'phone', phone } : { searchField: 'phone', phone }
        )
        this.scheduleSearch(phone)
    }

    // each box completes its own field, so the picked value is that field's — not always the phone.
    // two people at the same agency give the agent-name box two identical options and
    // the first wins. Phone and email are unique, and the label shows the whole person, so the
    // coordinator can see the clash — carry the row index on the option if that ever bites
    handleMatchSelect = (field: SearchField) => (value: any) => {
        const picked = String(value)
        const match: Match = this.state.matches.filter((u: Match) => valueOf(u, field) === picked)[0]
        if (!match) return

        // the mentor arrives as a free-text name, not an id — map it onto the dropdown by name so it
        // can be preselected as the default. An old or misspelled name matches nothing; then it stays empty
        const wanted = String(match.mentor || '').trim()
        const mentor = wanted
            ? this.state.mentorsList.filter((m: any) => String(m.name || '').trim() === wanted)[0]
            : null

        const isUser = match.source !== 'meeting'

        this.setState({
            selectedUser: isUser ? match : null,
            matchedContact: isUser ? null : match,
            // the box picked from is the identifying one, so it is the box left editable
            searchField: field,
            phone: withLeadingZero(match.phone),
            fullName: fullNameOf(match),
            email: match.email || '',
            address: match.address || '',
            agentName: match.agent_name || '',
            mentorId: mentor ? mentor.id : undefined,
            matches: [],
            meetings: []
        })

        // both kinds have a history worth seeing before booking another: a registered agent
        // by id, an off-system contact by the details their past meetings were saved under
        if (isUser && match.id) this.loadAgentHistory(match.id)
        else this.loadAgentHistory(0, this.contactQuery(match))
    }

    handleDateChange = value => {
        const { meetingTime, status } = this.state
        // keep the previously picked hour when only the date changes
        const merged =
            value && meetingTime
                ? value
                      .clone()
                      .hour(meetingTime.hour())
                      .minute(meetingTime.minute())
                : value

        this.setState({
            meetingTime: merged,
            status: merged && !isMeetingDocumentable(merged) ? 'not_documented' : status
        })
    }

    // fill in "now" as the actual value when the time picker opens, so it counts as picked
    handleTimeOpenChange = open => {
        const { meetingTime, hourPicked } = this.state
        if (open && !hourPicked) {
            this.handleTimeChange(meetingTime || moment())
        }
    }

    handleTimeChange = value => {
        const { meetingTime, status } = this.state
        // keep the previously picked date when only the hour changes
        const base = meetingTime || moment()
        const merged = value
            ? base
                  .clone()
                  .hour(value.hour())
                  .minute(value.minute())
            : null

        this.setState({
            meetingTime: merged,
            hourPicked: !!value,
            status: merged && !isMeetingDocumentable(merged) ? 'not_documented' : status
        })
    }

    // the form resets, but the mentors list and (in agent mode) the history are worth keeping
    resetForm = (extra?: any) => {
        const { mentorsList, meetings } = this.state
        this.setState({
            ...EMPTY_FORM,
            mentorsList,
            // EMPTY_FORM clears mentorId, but the agent's own mentor is a default, not user input
            mentorId: this.agentDefaultMentorId(mentorsList),
            meetings: this.isAgentMode ? meetings : [],
            historyLoading: false,
            editingMeetingId: null,
            historyVisible: false,
            ...extra
        })
    }

    handleClose = () => {
        clearTimeout(this.searchTimer)
        this.resetForm()
        this.props.onClose()
    }

    handleSubmit = () => {
        const { selectedUser, fullName, phone, email, address, agentName, mentorId, meetingTime, status } = this.state

        // ponytail: on localhost, always use a random test coordinator id, even if the real user is a coordinator
        const isLocalhost = window.location.hostname.includes('localhost')
        const createdBy = isLocalhost ? (Math.random() < 0.5 ? 100000472 : 100000471) : this.props.authStore.id

        // the dropdown is the only mentor input, so save exactly what it holds — no fallback to
        // agentInfo.mentor_id, which would override a mentor the coordinator deliberately left empty
        const payload: any = {
            mentor_id: mentorId,
            meeting_time: meetingTime.format('YYYY-MM-DD HH:mm:ss'),
            status,
            created_by: createdBy
        }

        // the agency/agent name is free text either way — an existing user can still belong to an agency
        if (agentName.trim()) payload.contact_agent_name = agentName.trim()

        const agentId = this.isAgentMode ? this.agentId() : selectedUser ? selectedUser.id : null

        if (agentId) {
            // a registered agent is stored as a normal agent meeting
            payload.agent_id = agentId
            if (address.trim()) payload.contact_address = address.trim()
        } else {
            // anyone else — including a contact recovered from a past meeting — rides on the contact columns
            payload.contact_name = fullName.trim()
            payload.contact_phone = phone.trim()
            payload.contact_email = email.trim()
            payload.contact_address = address.trim()
        }

        this.setState({ saving: true })
        mentorsService
            .createMeeting(payload)
            .then(() => {
                if (!this._isMounted) return
                message.success('הפגישה נוספה')
                if (this.props.onCreated) this.props.onCreated()

                // the modal stays open after submit either way — the user closes it manually.
                // keep the agent/person details filled in, only clear the meeting-specific fields,
                // so a coordinator can log another meeting for the same person without retyping it
                this.setState({ meetingTime: null, hourPicked: false, status: 'not_documented', saving: false })
                // the meeting just added belongs in the history, for a contact as much as an agent
                this.reloadHistory()
            })
            .catch(error => {
                if (!this._isMounted) return
                this.setState({ saving: false })
                alertPopup({ type: 'error', title: 'שגיאה בהוספת פגישה', body: meetingErrorBody(error, '') })
            })
    }

    // what the server looks a contact up by when there is no agent id. Taken from the match
    // rather than the form so a half-typed edit cannot narrow the search to nothing
    contactQuery = (match: Match) => ({
        phone: withLeadingZero(match.phone),
        name: fullNameOf(match),
        email: match.email || ''
    })

    // someone is in play either because this is the agent page, or a search matched a person —
    // registered or not. A contact off the system still has meetings under their details
    hasAgent = () => this.isAgentMode || !!this.state.selectedUser || !!this.state.matchedContact

    openHistory = () => {
        this.setState({ historyVisible: true })
        // agent mode preloads on open, but a search match may have gone stale since it was picked
        this.reloadHistory()
    }

    // the history table edits rows in place, so it reloads itself and tells the parent
    historyAgentId = () => {
        const { selectedUser } = this.state
        return this.isAgentMode ? this.agentId() : selectedUser ? selectedUser.id : null
    }

    // by id when there is one, otherwise by the matched contact's details
    reloadHistory = () => {
        const { matchedContact } = this.state
        const agentId = this.historyAgentId()
        if (agentId) return this.loadAgentHistory(agentId)
        if (matchedContact) return this.loadAgentHistory(0, this.contactQuery(matchedContact))
    }

    afterHistoryChange = () => {
        this.reloadHistory()
        if (this.props.onCreated) this.props.onCreated()
    }

    handleStatusChange = (meeting, status) => {
        this.setState({ editingMeetingId: null })
        mentorsService
            .updateMeeting(meeting.id, { status })
            .then(this.afterHistoryChange)
            .catch(error =>
                alertPopup({ type: 'error', title: 'שגיאה בעדכון פגישה', body: meetingErrorBody(error, '') })
            )
    }

    handleDeleteMeeting = meeting => {
        mentorsService
            .deleteMeeting(meeting.id)
            .then(this.afterHistoryChange)
            .catch(error =>
                alertPopup({ type: 'error', title: 'שגיאה במחיקת פגישה', body: meetingErrorBody(error, '') })
            )
    }

    historyColumns = (now: moment.Moment): ColumnProps<any>[] => [
        {
            title: 'תאריך',
            dataIndex: 'meeting_time',
            width: 220,
            align: 'right',
            render: value => (
                <span>
                    {moment(value).format('DD/MM/YYYY HH:mm')}
                    {moment(value).isAfter(now) ? <FutureMeetingTag /> : null}
                </span>
            )
        },
        {
            title: 'מנטור',
            dataIndex: 'mentor',
            width: 160,
            align: 'right',
            render: mentor => (mentor ? mentor.name : '—')
        },
        {
            title: 'התקיימה',
            dataIndex: 'status',
            width: 140,
            align: 'center',
            render: (status, record) =>
                renderStatusCell({
                    status,
                    recordId: record.id,
                    editingMeetingId: this.state.editingMeetingId,
                    isFuture: !isMeetingDocumentable(record.meeting_time, now),
                    onEdit: id => this.setState({ editingMeetingId: id }),
                    onCancelEdit: () => this.setState({ editingMeetingId: null }),
                    onChange: value => this.handleStatusChange(record, value)
                })
        },
        {
            title: '',
            dataIndex: 'actions',
            width: 50,
            align: 'center',
            render: (_, record) => (
                // icon-only: a red block per row shouted louder than the meeting data itself
                <Popconfirm
                    title="האם למחוק את הפגישה?"
                    okText="מחיקה"
                    cancelText="ביטול"
                    okType="danger"
                    overlayClassName="mentor-meetings-delete-popconfirm"
                    onConfirm={() => this.handleDeleteMeeting(record)}
                >
                    <Tooltip title="מחיקת פגישה">
                        <Button
                            shape="circle"
                            size="small"
                            icon="delete"
                            style={{ color: '#ff4d4f', borderColor: 'transparent', background: 'transparent' }}
                        />
                    </Tooltip>
                </Popconfirm>
            )
        }
    ]

    renderPersonCard() {
        const { fullName, phone, email, agentName, matches, selectedUser, matchedContact, searchField } = this.state

        // a picked match owns every detail, so every box locks except the one the person was
        // identified from — editing that one drops the match and frees the rest
        const hasMatch = !!selectedUser || !!matchedContact
        const lockedExcept = (field: SearchField) => hasMatch && searchField !== field

        // every box shows the same label — the whole person, in one fixed order — but the value
        // it writes is still its own field, so picking in the name box leaves a name in it.
        // optionLabelProp defaults to 'children' in antd 3.23.4, which would drop the whole
        // label into the input; 'value' keeps just the completed field there.
        // combobox mode also rejects a non-string option value, hence the coercion
        const optionsFor = (field: SearchField) =>
            matches
                .map((u: Match, i: number) => {
                    const own = String(valueOf(u, field) || '')
                    // a match with nothing in this box cannot complete it — offering a blank
                    // option would clear the field on pick, and antd cannot key on ''
                    if (!own) return null

                    return (
                        <AutoOption key={u.id ? `u${u.id}` : `c${i}`} value={own}>
                            {LABEL_ORDER.map(f => valueOf(u, f))
                                .filter(v => v)
                                .join(' - ')}
                        </AutoOption>
                    )
                })
                .filter(option => option)

        // the four boxes differ only in label, icon and which state field they write
        const termField = (field: SearchField, value: string, props: any) => (
            <div style={style.field}>
                <label style={style.label}>
                    <Icon type={props.icon} style={style.iconFix} /> {props.label}
                </label>
                <AutoComplete
                    value={value}
                    // a locked field must not offer options either
                    dataSource={props.disabled ? [] : optionsFor(field)}
                    disabled={props.disabled}
                    optionLabelProp="value"
                    className="commission-select"
                    dropdownClassName="commission-dropdown meeting-match-dropdown"
                    // the phone box strips to digits, so it brings its own change handler
                    onChange={props.onChange || this.handleTermChange(field)}
                    onSelect={this.handleMatchSelect(field)}
                    placeholder={props.placeholder}
                    style={{ width: '100%' }}
                />
            </div>
        )

        return (
            <div style={style.addCard}>
                <div style={style.addCardTitle}>
                    <Icon type="user-add" style={{ ...style.iconFix, color: '#1890ff' }} />
                    <span>פרטי הסוכן</span>
                </div>

                <div style={style.addGrid}>
                    {/* one control does both jobs: free-text phone entry, and the dropdown of people holding it */}
                    {termField('phone', phone, {
                        icon: 'phone',
                        label: 'טלפון *',
                        placeholder: 'מספר טלפון',
                        disabled: lockedExcept('phone'),
                        onChange: this.handlePhoneChange
                    })}

                    {termField('fullName', fullName, {
                        icon: 'user',
                        label: 'שם מלא *',
                        placeholder: 'שם מלא',
                        disabled: lockedExcept('fullName')
                    })}

                    {/* the person's own name is not always the business name — an agency gets its own field.
                        never locked, even with a match picked: the agency is free text on the meeting itself
                        (contact_agent_name), so it stays editable without dropping the match */}
                    {termField('agentName', agentName, {
                        icon: 'shop',
                        label: 'שם סוכן',
                        placeholder: 'שם סוכן'
                    })}

                    {termField('email', email, {
                        icon: 'mail',
                        label: 'אימייל',
                        placeholder: 'אימייל',
                        disabled: lockedExcept('email')
                    })}
                </div>

                {this.renderAddressRow()}

                {selectedUser && (
                    <div style={style.matchedNote}>
                        <Icon type="check-circle" style={style.iconFix} />
                        <span>{`הפרטים שייכים למשתמש קיים (${fullNameOf(selectedUser)}). לשינוי — ערכו את ${
                            SEARCH_FIELD_LABELS[searchField]
                        }`}</span>
                    </div>
                )}

                {/* a past-meeting match is not a registered user — say so, or the coordinator assumes it is */}
                {matchedContact && (
                    <div style={{ ...style.matchedNote, color: '#fa8c16' }}>
                        <Icon type="info-circle" style={style.iconFix} />
                        <span>{`הפרטים הושלמו מפגישה קודמת. אין משתמש רשום עם פרטים אלה. לשינוי — ערכו את ${SEARCH_FIELD_LABELS[searchField]}`}</span>
                    </div>
                )}
            </div>
        )
    }

    renderAddressRow() {
        const { address, selectedUser } = this.state
        // for someone outside the system the address is the only way to find the meeting, so it is required
        const optional = this.isAgentMode || !!selectedUser
        return (
            <div style={{ ...style.addGrid, marginTop: '14px' }}>
                <div style={style.field}>
                    {/* prefilled from the match, but still editable — the meeting address can differ */}
                    <label style={style.label}>
                        <Icon type="environment" style={style.iconFix} /> {optional ? 'כתובת' : 'כתובת *'}
                    </label>
                    <Input
                        value={address}
                        placeholder="כתובת"
                        onChange={e => this.setState({ address: e.target.value })}
                    />
                </div>
            </div>
        )
    }

    renderMeetingCard() {
        const { mentorsList, mentorId, meetingTime, hourPicked, status, saving } = this.state
        // no date yet also blocks the toggle, so there is nothing to mark against
        const isFuture = !meetingTime || !isMeetingDocumentable(meetingTime)
        const hasAgent = this.hasAgent()

        return (
            <div style={{ ...style.addCard, marginBottom: '0px' }}>
                <div style={style.addCardTitle}>
                    <Icon type="plus" style={{ ...style.iconFix, color: '#1890ff' }} />
                    <span>פגישה חדשה</span>
                </div>

                <div style={style.addGrid}>
                    <div style={{ ...style.field, flex: '0 0 160px' }}>
                        <label style={style.label}>
                            <Icon type="user" style={style.iconFix} /> {'מנטור *'}
                        </label>
                        <Select
                            placeholder="בחירת מנטור"
                            value={mentorId}
                            // the matched/agent mentor is only a default — always stays changeable
                            onChange={value => this.setState({ mentorId: value })}
                            className="commission-select"
                            dropdownClassName="commission-dropdown"
                            style={{ width: '160px' }}
                        >
                            {mentorsList.map((m: any) => (
                                <Option key={m.id} value={m.id}>
                                    {m.name}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div style={style.field}>
                        <label style={style.label}>
                            <Icon type="calendar" style={style.iconFix} /> תאריך *
                        </label>
                        <DatePicker
                            format="DD/MM/YYYY"
                            placeholder="תאריך פגישה"
                            value={meetingTime}
                            style={{ width: '100%' }}
                            disabledDate={isBeforeMeetingsStart}
                            onChange={this.handleDateChange}
                        />
                    </div>

                    <div style={style.field}>
                        <label style={style.label}>
                            <Icon type="clock-circle" style={style.iconFix} /> שעה *
                        </label>
                        <TimePicker
                            format="HH:mm"
                            minuteStep={15}
                            placeholder="שעת פגישה"
                            popupClassName="mentor-meetings-time-picker"
                            value={hourPicked ? meetingTime : null}
                            style={{ width: '100%' }}
                            onChange={this.handleTimeChange}
                            onOpenChange={this.handleTimeOpenChange}
                        />
                    </div>
                </div>

                {/* agent mode has no person card, so the address field belongs here instead */}
                {this.isAgentMode && this.renderAddressRow()}

                <div style={style.addFooter}>
                    <Tooltip
                        title={isFuture ? (!meetingTime ? 'יש לבחור תאריך פגישה' : 'ניתן לסמן רק מיום הפגישה') : ''}
                    >
                        <div style={{ ...style.statusToggle, ...(isFuture ? style.statusToggleDisabled : {}) }}>
                            {OCCURRED_CHOICES.map((key, i) => (
                                <button
                                    key={key}
                                    type="button"
                                    disabled={isFuture}
                                    onClick={() => this.setState({ status: key })}
                                    style={{
                                        ...style.statusToggleBtn,
                                        ...(i === 0 ? style.statusToggleBtnFirst : {}),
                                        ...(status === key
                                            ? style.statusToggleBtnSelected
                                            : style.statusToggleBtnUnselected)
                                    }}
                                >
                                    {STATUS_LABELS[key]}
                                </button>
                            ))}
                        </div>
                    </Tooltip>

                    {/* disabled until valid, so the user never clicks into an error popup */}
                    <Tooltip title={this.submitHint()}>
                        <Button
                            type="primary"
                            icon="plus"
                            onClick={this.handleSubmit}
                            loading={saving}
                            disabled={!this.canSubmit()}
                        >
                            הוספת פגישה
                        </Button>
                    </Tooltip>

                    {/* history moved out of this modal — only reachable once a person has been matched,
                        registered or not. auto margin pushes it to the far end of the row, away from submit */}
                    <Tooltip title={hasAgent ? '' : 'יש לבחור סוכן או איש קשר'}>
                        <Button icon="history" disabled={!hasAgent} onClick={this.openHistory} style={style.historyBtn}>
                            היסטוריית פגישות
                        </Button>
                    </Tooltip>
                </div>
            </div>
        )
    }

    canSubmit = () => {
        const { fullName, phone, address, mentorId, meetingTime, hourPicked, selectedUser } = this.state
        if (!mentorId || !meetingTime || !hourPicked) return false
        if (this.isAgentMode) return true
        if (!fullName.trim() || !phone.trim()) return false
        return !!selectedUser || !!address.trim()
    }

    submitHint = () => {
        if (this.canSubmit()) return ''
        if (this.isAgentMode) return 'יש לבחור מנטור, תאריך ושעה'
        return this.state.selectedUser
            ? 'יש למלא שם, טלפון, מנטור, תאריך ושעה'
            : 'יש למלא שם, טלפון, כתובת, מנטור, תאריך ושעה'
    }

    render() {
        const now = moment()

        return (
            <>
                <Modal
                    title={this.isAgentMode ? 'פגישות מנטור' : 'פגישה עם סוכן חדש'}
                    className="mentor-meetings-modal"
                    visible={this.props.visible}
                    onCancel={this.handleClose}
                    footer={null}
                    width={720}
                    // antd 3.23.4 supports `centered` natively: vertical-align middle in .ant-modal-wrap.
                    // capping the body height keeps it centered instead of overflowing off-screen when tall
                    centered
                    destroyOnClose
                    bodyStyle={{
                        direction: 'rtl',
                        textAlign: 'right',
                        overflowY: 'auto'
                    }}
                >
                    <style>{COMMISSION_SELECT_STYLES + MATCH_DROPDOWN_STYLES}</style>
                    {/* this select sits flush against its label, so it drops the shared right gap */}
                    <style>{`
                    .commission-select .ant-select-selection--single .ant-select-selection__rendered {
                        margin-right: 0px;
                    }
                    .commission-select .ant-select-selection-selected-value {
                        direction: rtl;
                        padding-right: 10px;
                    }
                    .mentor-meetings-modal .ant-calendar-picker-clear.anticon {
                        position: absolute;
                            top: 15px;
                    }
                    .mentor-meetings-modal .anticon.anticon-close-circle.ant-time-picker-clear {
                        position: absolute;
                            top: 15px;
                    }
                `}</style>

                    {!this.isAgentMode && this.renderPersonCard()}
                    {this.renderMeetingCard()}
                </Modal>
                {this.renderHistoryModal(now)}
            </>
        )
    }

    // its own popup, so the meeting form stays short and vertically centered
    renderHistoryModal(now: moment.Moment) {
        const { meetings, historyLoading, historyVisible } = this.state
        return (
            <Modal
                title="היסטוריית פגישות"
                className="mentor-meetings-modal"
                visible={historyVisible}
                onCancel={() => this.setState({ historyVisible: false, editingMeetingId: null })}
                footer={null}
                width={720}
                centered
                destroyOnClose
                bodyStyle={{ direction: 'rtl', textAlign: 'right', maxHeight: '90vh', overflowY: 'auto' }}
            >
                <div style={style.historyTableWrap}>
                    <Table
                        className="mentor-meetings-history-table"
                        rowKey="id"
                        dataSource={meetings}
                        columns={this.historyColumns(now)}
                        pagination={{ pageSize: 5, size: 'small', hideOnSinglePage: true }}
                        loading={historyLoading}
                        size="middle"
                        locale={{ emptyText: 'לא נמצאו פגישות עבור סוכן זה' }}
                    />
                </div>
            </Modal>
        )
    }
}

const style: { [key: string]: React.CSSProperties } = {
    // cancels the global .anticon { top: -0.2rem } rule so icons sit on the text baseline here
    iconFix: { top: '1px' },
    addCard: {
        background: '#f5f9ff',
        border: 'solid 1px #d6e4ff',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px'
    },
    addCardTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '15px',
        fontWeight: 600,
        marginBottom: '14px',
        color: '#1890ff'
    },
    addGrid: { display: 'flex', gap: '14px' },
    field: { flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#8c8c8c' },
    matchedNote: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '12px',
        fontSize: '13px',
        color: '#52c41a'
    },
    // one joined rectangle, not two floating pills — reads as a single control with two settings
    statusToggle: {
        display: 'inline-flex',
        border: 'solid 1px #d9d9d9',
        borderRadius: '4px',
        overflow: 'hidden'
    },
    statusToggleDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    statusToggleBtn: {
        display: 'flex',
        alignItems: 'center',
        height: '32px',
        paddingInline: '14px',
        fontSize: '13px',
        border: 'none',
        borderInlineStart: 'solid 1px #d9d9d9',
        cursor: 'pointer'
    },
    statusToggleBtnFirst: { borderInlineStart: 'none' },
    // same blue no matter which of the two is picked — color here means "selected", not which choice it is
    statusToggleBtnSelected: { background: '#1890ff', color: 'white' },
    statusToggleBtnUnselected: { background: '#ffffff', color: '#595959' },
    addFooter: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        justifyContent: 'space-between',
        marginTop: '16px',
        paddingTop: '14px',
        borderTop: 'solid 1px #f0f0f0'
    },
    // RTL row: an auto start-margin eats the free space, parking this button on the left edge
    historyBtn: { },
    historyTableWrap: {
        background: 'white',
        border: 'solid 1px #f0f0f0',
        borderRadius: '6px',
        overflow: 'hidden'
    }
}

export default inject('authStore')(observer(MeetingModal))
